import { datadogLogs, type LogsEvent, type StatusType } from "@datadog/browser-logs"
import { GetTokenFromCookies } from "src/lib/utils/cookies"

/**
 * Browser Logs に送るログの種類。ブラウザのエラー（例外・console.error・通信エラー）は
 * SDK の forwardErrorsToLogs が自動で送るので、ここでは明示的に送るものだけを定義する。
 */
const LOG_TYPE = {
  pageView: "page_view",
  apiCall: "api_call",
} as const

// token を伏せる基準は smooth_liff / smooth_partner と同じにしている（例はそちらの path）。
// 数字と英字が混ざった 16 文字以上のセグメントは token（liff の共有書類の documentToken 等）とみなして伏せる。
// 数字だけの ID（proposal_id 等）や、英字だけのリソース名は残す。
const MIXED_TOKEN_SEGMENT = /^(?=.*\d)(?=.*[A-Za-z])[A-Za-z0-9_-]{16,}$/
// documentToken（urlsafe_base64 の 43 文字）は約 1,300 本に 1 本数字を含まないため、長さでも拾う。
// 長いリソース名（partner の company-information-form-complete 等）は英小文字を - / _ でつないだ形なので残す。
const LONG_TOKEN_SEGMENT = /^[A-Za-z0-9_-]{32,}$/
const RESOURCE_NAME = /^[a-z]+(?:[_-][a-z]+)*$/

const isTokenLike = (segment: string) =>
  MIXED_TOKEN_SEGMENT.test(segment) ||
  (LONG_TOKEN_SEGMENT.test(segment) && !RESOURCE_NAME.test(segment))

export const maskPath = (path: string): string =>
  path
    .split("/")
    .map((segment) => (isTokenLike(segment) ? ":token" : segment))
    .join("/")

const sanitizeUrlParts = (url: string): { url: string; path: string } => {
  try {
    const parsed = new URL(url, window.location.origin)
    const path = maskPath(parsed.pathname)
    return { url: `${parsed.origin}${path}`, path }
  } catch {
    const path = maskPath(url.split(/[?#]/)[0])
    return { url: path, path }
  }
}

/** query と hash を落とし、path の token を伏せる。認可 code や access_token が URL に載ることがあるため */
export const sanitizeUrl = (url: string): string => sanitizeUrlParts(url).url

const URL_IN_TEXT = /https?:\/\/[^\s"'<>]+/g

// SDK の通信エラーログは message に `Fetch error GET <URL>` の形で URL をそのまま入れる（相対 URL のこともある）
const sanitizeMessage = (message: string, rawHttpUrl?: string): string => {
  const replaced = rawHttpUrl ? message.split(rawHttpUrl).join(sanitizeUrl(rawHttpUrl)) : message
  return replaced.replace(URL_IN_TEXT, (url) => sanitizeUrl(url))
}

export const sanitizeLogsEvent = (event: LogsEvent): boolean => {
  event.message = sanitizeMessage(event.message, event.http?.url)
  event.view.url = sanitizeUrl(event.view.url)
  if (event.view.referrer) event.view.referrer = sanitizeUrl(event.view.referrer)
  if (event.http?.url) event.http.url = sanitizeUrl(event.http.url)
  return true
}

/**
 * cookie の Google ID token から email を取り出す。smooth_api は同じ token の email で smoother を特定している。
 * ログに載せるだけなので署名は検証しない。
 */
export const smootherEmailFromToken = (token: string | null): string | undefined => {
  const payload = token?.split(".")[1]
  if (!payload) return undefined
  try {
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/")
    const { email } = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "=")))
    return typeof email === "string" ? email : undefined
  } catch {
    return undefined
  }
}

/**
 * smooth_api のログ（lograge）と同じ属性名 @smoother_email で、操作した smoother を載せる。
 * サインイン・サインアウト・token の更新に追従するよう、送るたびに cookie から読む。
 */
export const beforeSendLogs = (event: LogsEvent): boolean => {
  // email の付与が失敗しても URL は伏せた状態で送るよう、サニタイズを先に済ませる
  const result = sanitizeLogsEvent(event)
  try {
    // GetTokenFromCookies は cookie 全体を decodeURIComponent するため、他の cookie の不正な % で throw しうる
    const smootherEmail = smootherEmailFromToken(GetTokenFromCookies())
    if (smootherEmail) event.smoother_email = smootherEmail
  } catch {
    // email が付かないだけでログ自体は送る
  }
  return result
}

export const logPageView = ({ route, fromRoute }: { route: string; fromRoute?: string }) => {
  datadogLogs.logger.info(`page_view ${route}`, {
    log_type: LOG_TYPE.pageView,
    page: { route, from_route: fromRoute },
  })
}

const apiCallStatus = (statusCode: number): StatusType => {
  if (statusCode === 0 || statusCode >= 500) return "error"
  if (statusCode >= 400) return "warn"
  return "info"
}

type ApiCallLog = {
  method: string
  url: string
  /** 通信自体が失敗してレスポンスがない場合は 0 */
  statusCode: number
  durationMs: number
  traceId: string
  error?: unknown
}

export const logApiCall = ({ method, url, statusCode, durationMs, traceId, error }: ApiCallLog) => {
  const { url: sanitizedUrl, path } = sanitizeUrlParts(url)

  datadogLogs.logger.log(
    `api_call ${method} ${path} ${statusCode}`,
    {
      log_type: LOG_TYPE.apiCall,
      http: {
        method,
        url: sanitizedUrl,
        url_details: { path },
        status_code: statusCode,
        duration_ms: Math.round(durationMs),
      },
      // Datadog の予約属性。API 側のログ・APM の trace と同じ ID で紐付く
      dd: { trace_id: traceId },
    },
    apiCallStatus(statusCode),
    error instanceof Error ? error : undefined
  )
}
